/* ----------------- React --------------- */
import { useEffect, useState } from "react";

/* ----------------- Hooks --------------- */
import useSessionStore from "../hooks/useSessionStore";

/* ----------------- Third Party Libraries --------------- */
import { useLocalSearchParams } from "expo-router";

import { events } from "@constants/tracking";
import { isWeb } from "@helpers/platform";
import useAnalytics from "@hooks/useAnalytics";
/* ----------------- Types --------------- */
import PassageStemModal from "../components/PassageStemModal";
import { MAPPED_ITEM_TYPES } from "../constants";
import { getIsSlidesHasPassage, getSlidesData } from "../helpers";
import composeSessionMd from "../helpers/composeSessionMd";
import { useStimulusStore } from "../hooks/useStimulusStore";

type Props = {
	showReadMore?: boolean;
	propToggleShowReadMore?: () => void;
	passageStemModalRef?: React.RefObject<{
		scrollToHighlightedText: () => void;
	}>;
};
export const PassageStemModalContainer = ({
	showReadMore,
	propToggleShowReadMore,
	passageStemModalRef,
}: Props) => {
	const showStimulusModal = useStimulusStore(
		(state) => state.showStimulusModal,
	);
	const setShowStimulusModal = useStimulusStore(
		(state) => state.setShowStimulusModal,
	);
	const { explicitCallTrackCustomEvent } = useAnalytics();

	const { index, prev } = useLocalSearchParams<{
		index: string;
		prev?: string;
	}>();

	const [isStart, setIsStart] = useState<boolean>(false);

	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const sessionItems = useSessionStore((state) => state.sessionItems);
	const currentItem = sessionItems[currentItemIndex];
	const updateItemStates = useSessionStore((state) => state.updateItemStates);
	const answers = useSessionStore((state) => state.answers);
	const currentAnswer = answers?.get(currentItem.id!);
	const itemState = useSessionStore((state) =>
		state.itemStates?.get(currentItem.id!),
	);

	const {
		metadata,
		stimulus: parentStimulus,
		currentStimulusItemIndex: parentIndex,
		id: itemId,
	} = currentItem;

	const parentTitle =
		parentStimulus?.content.passage?.title || parentStimulus?.content.className;
	const type = metadata?.type!;
	const skill = metadata?.skill;
	const parentSlides = parentStimulus?.content.slides;
	const parentProfessor = parentStimulus?.content.professor;

	const slideshowData = getSlidesData(parentSlides);
	const isSlidesHasPassage = getIsSlidesHasPassage(parentSlides);
	const parentStem = composeSessionMd({
		content: parentStimulus?.content.passage,
		highlights: currentItem.content?.highlights,
		insertText: currentItem.content?.insertSentence,
		paragraphNumber: currentItem.content?.paragraphNumber,
		choices: currentItem.content?.choices,
		shouldFormat: !isStart,
		selectedChoiceIds: itemState?.selectedChoiceIds,
	});

	const parentInstruction = parentStimulus?.content.instruction;
	const parentAudio = parentStimulus?.content.media?.find(
		(media) => media.type === "audio" || media.type === "AUDIO",
	)?.url;
	const parentVideo = parentStimulus?.content.media?.find(
		(media) => media.type === "video" || media.type === "VIDEO",
	)?.url;

	const itemType = MAPPED_ITEM_TYPES[type];

	useEffect(() => {
		setShowStimulusModal(showReadMore);
	}, [showReadMore, setShowStimulusModal]);

	useEffect(() => {
		if (itemId && !isWeb) {
			explicitCallTrackCustomEvent({
				eventName: showStimulusModal
					? events.sessionStimulusOpened
					: events.sessionStimulusClosed,
				itemId: itemId,
				stimulusType: type,
			});
		}
	}, [itemId, type, showStimulusModal, explicitCallTrackCustomEvent]);

	useEffect(() => {
		if (
			parentIndex === 1 &&
			itemId &&
			prev !== "true" &&
			!isWeb &&
			currentItemIndex === 0
		) {
			setShowStimulusModal(true);
			setIsStart(true);
		}
	}, [parentIndex, itemId, prev, setShowStimulusModal, currentItemIndex]);

	const toggleShowReadMore = () => {
		setIsStart(false);
		setShowStimulusModal(!showStimulusModal);
		propToggleShowReadMore?.();
	};

	const handleClickInsertableText = (choiceId: string) => {
		updateItemStates({
			itemId: currentItem.id!,
			selectedChoiceId: choiceId,
			multiSelect: itemType === "SATA",
		});
	};

	const disableInsertableText = currentAnswer != null;

	return (
		<PassageStemModal
			showModal={showStimulusModal}
			toggleShowModal={toggleShowReadMore}
			mode={isStart ? "modal" : "sheet"}
			parentStem={parentStem}
			parentTitle={parentTitle}
			insertText={currentItem.content?.insertSentence}
			slideshowData={slideshowData}
			parentIndex={parentIndex}
			disableInsertableText={disableInsertableText}
			parentInstruction={parentInstruction}
			parentAudio={parentAudio}
			parentVideo={parentVideo}
			skill={skill}
			slides={parentSlides}
			isSlidesHasPassage={isSlidesHasPassage}
			handleClickInsertableText={handleClickInsertableText}
			professor={parentProfessor}
			ref={passageStemModalRef}
			itemId={itemId}
		/>
	);
};
